import React, { useState } from 'react';
import { CronofyOptions } from '../../../../../components/cronofy/CronofyOptions';
import { InterviewStage } from '../../../../../entities/applicant_tracking/InterviewStage';
import ScheduleInterviewPage, { SchedulingSettings } from '../index';
import { User } from '../../../../../entities/User';
import candidateSchedulingImage from '../../../../../images/candidate-experience-mailer-new-survey.png';
import {
  InterviewDatePickerFooter,
  UpsertSelfScheduleInterviewPropTypes,
} from '../InterviewDatePicker/InterviewDatePickerFooter';
import { InterviewCandidateUserService } from '../../../../../services/applicant_tracking/InterviewCandidateUserService';
import { VcsProvider } from '../../../../../enums/IntegrationProvider/VcsProvider';
import { appendCurrentRedirectUri } from '../../../../../utils/url';
import { ShareTeamAvailabilityFooter } from '../ShareTeamAvailabilityFooter';
import { appendDateQueryParamsToUrl } from '../utils/buildDateQueryParams';
import { addReturnTitle } from '../../../../../utils/applicant_tracking/actions/schedulingActions';
import { Candidate } from '../../../../../entities/applicant_tracking/Candidate';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../../../../config/queryClient';
import { FeatureFlagContext } from '../../../../../contexts/FeatureFlagContext';

interface PropTypes {
  applicationId: number;
  candidate: Candidate;
  cronofyDatePickerOptions: CronofyOptions;
  currentUser: User;
  interviewStage: InterviewStage;
  meetingProviders: VcsProvider[];
  defaultMeetingProvider: VcsProvider;
  interviewTemplateId: number | null;
  personalizingSchedulingInvitesV1FfEnabled: boolean;
  personalizingSchedulingInvitesV2FfEnabled: boolean;
}

export default function CandidateScheduleInterview(props: PropTypes) {
  const [schedulingSettings, setSchedulingSettings] =
    useState<SchedulingSettings>(null);
  const onConfirm = async (data: UpsertSelfScheduleInterviewPropTypes) => {
    try {
      const interviewCandidateUser =
        await InterviewCandidateUserService.createSelfSchedule(
          props.applicationId,
          props.interviewStage.id,
          data.interviewerUserIds,
          data.meetingProvider,
          data.durationMinutes,
          data.dateRange,
          data.scheduleFromHours,
          data.timezone,
          data.meetingOrganizer.id,
          data.calendarEventTemplateId,
          data.calendarEventTemplateDto,
        );

      const path = appendCurrentRedirectUri(
        appendDateQueryParamsToUrl(
          `/applicant_tracking/interview_candidate_users/${interviewCandidateUser.id}`,
          data.scheduleFromHours,
          data.dateRange,
          data.timezone,
        ),
      );
      window.location.href = addReturnTitle(path);
    } catch (e) {
      window.location.href = appendCurrentRedirectUri(
        `/applicant_tracking/interview_candidate_users/schedule/error`,
      );
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagContext.Provider
        value={{
          personalizingSchedulingInvitesV1FfEnabled:
            props.personalizingSchedulingInvitesV1FfEnabled,
          personalizingSchedulingInvitesV2FfEnabled:
            props.personalizingSchedulingInvitesV2FfEnabled,
        }}
      >
        <ScheduleInterviewPage
          meetingOrganizer={props.currentUser}
          applicationIds={[props.applicationId]}
          title={'Let the candidate schedule the interview'}
          cronofyDatePickerOptions={props.cronofyDatePickerOptions}
          candidate={props.candidate}
          participants={[props.currentUser]}
          interviewStage={props.interviewStage}
          meetingProviders={props.meetingProviders}
          meetingProvider={props.defaultMeetingProvider}
          customImage={candidateSchedulingImage}
          onConfirm={(_data) => null}
          candidateView={false}
          selfScheduleView={true}
          allowChangingMeetingOrganizer={true}
          allowIgnoreParticipantCalendar={false}
          interviewTemplateId={props.interviewTemplateId}
          setSchedulingSettings={setSchedulingSettings}
          interviewDatePickerFooter={
            <InterviewDatePickerFooter
              buttonText='Confirm Availability'
              description={
                <ShareTeamAvailabilityFooter
                  schedulingSettings={schedulingSettings}
                />
              }
              onConfirm={onConfirm}
            />
          }
          personalizingSchedulingInvitesV1FfEnabled={
            props.personalizingSchedulingInvitesV1FfEnabled
          }
        />
      </FeatureFlagContext.Provider>
    </QueryClientProvider>
  );
}
